import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React, { useState } from "react";
import styled from "styled-components";
import { APIProvider } from "@vis.gl/react-google-maps";

// Imported assets
import { CheckmarkIcon } from "../../../../assets/icons/svgs/Icons";
import EditIconBlack from "../../../../assets/icons/Edit_BlackIcon.svg";
import EditIcon from "../../../../assets/icons/EditIcon";
import TrashIcon from "../../../../assets/icons/Delete_Red.svg";

// Imported components
import ColorMenu from "./components/ColorMenu";
import Label from "../../../../components/form/Label";
import Map from "./components/Map";
import PrimaryButton from "../../../../components/ui/PrimaryButton";

// Styled components
const AddNewUserButton = styled(PrimaryButton)`
  background: white;
  border: 1px solid #D9D9D9;
  border-radius: 8px;
  color: black;
  display: flex;
  flex-direction: row;
  font-size: 14px;
  padding: 0;
  position: absolute;
  right: 0;
  top: 0;
  width: 140px;
  z-index: 10;

  &:hover {
    background: lab(from white calc(l - 5) a b / 100);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 40px;
  justify-content: center;
  // margin: 0 20px;

  & > button {
    height: 40px;
    min-width: 80px;
  }
`;

const CancelButton = styled(PrimaryButton)`
  background: white;
  border: 1px solid #D9D9D9;
  border-radius: 8px;
  color: black;
  display: flex;
  flex-direction: row;
  width: 100px;

  &:hover {
    background: lab(from white calc(l - 5) a b / 100);
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%;
  padding: 40px;
  width: 100%;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ContentRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 40px;

  & .MuiInputBase-root {
    background: #ffffff;
  }

  @media (max-width: 1400px) {
    flex-direction: column;

    & > * {
      width: 100% !important;
    }
  }
`;

// const FormField = styled.div`
//   align-items: center;
//   display: flex;
//   flex-direction: column;
//   gap: 4px;
//   justify-content: flex-start;
//   position: relative;

//   & .MuiInputBase-root {
//     background: #ffffff;
//   }
// `;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  // margin: 20px 0 0 20px;
`;

const HeaderText = styled.span`
  align-content: center;
  font-family: Oxygen, Inter, Roboto, sans-serif;
  font-size: 36px;
  text-align: center;
`;

const Icon = styled.img`
  height: auto;
  margin-right: 10px;
  width: 20px;

  &:hover {
    cursor: pointer;
  }
`;

const EditIconContainer = styled(Icon)`
  width: max(16px, 1vw);
`;

const TrashIconContainer = styled(Icon)`
  margin-right: 0;
  width: max(16px, 1vw);
`;

const LocationPreviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%;
  width: 50%;
`;

const MapCanvas = styled.div`
  background: var(--terbine-light-gray);
  // border: 2px solid black;
  border-radius: 8px;
  display: flex;
  flex-direction: row;
  height: 50vh;
  position: relative;
  width: 100%;

  & .admin-settings-map > div {
    // border: 2px solid black;
    border-radius: 8px;
  }
`;

const ThemeButton = styled(PrimaryButton)`
  background: var(--terbine-blue);
  display: flex;
  flex-direction: row;
  width: 100px;

  &:hover {
    background: lab(from var(--terbine-blue) calc(l - 5) a b / 100);
  }
`;

const UsersInRegionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  position: relative;
  width: 50%;
`;

const UsersTable = styled.div`
  background: white;
  border-radius: 8px;
  // display: flex;
  // flex-direction: column;
  height: 100%;
`;

// const UsersTableHeader = styled.div`
//   display: flex;
//   flex-direction: column;
// `;

// TODO: Replace below hard-coded options for Main Display Region select box with options obtained from backend
const TEMP_MAIN_DISPLAY_OPTIONS = ["California", "Continental United States"];

// TODO: Replace below options for "Enabled Display Options" select box with options obtained from backend
const TEMP_ENABLED_DISPLAY_OPTIONS = [
  "System Health",
  "Network Averages",
  "Time of Day",
  "Severe Weather",
  "Status Legend",
];

// TODO: Get proper selection of colors for array below
// Color selection options for users
const TEMP_USER_COLOR_OPTIONS = [
  { label: "Aqua", hex: "#00ffff" },
  { label: "Cadet Blue", hex: "#5f9ea0" },
  { label: "Coral", hex: "#ff7f50" },
  { label: "Dark Salmon", hex: "#e9967a" },
  { label: "Goldenrod", hex: "#daa520" },
  { label: "Lawn Green", hex: "#7cfc00" },
  { label: "Light Cyan", hex: "#e0ffff" },
  { label: "Light Pink", hex: "#ffb6c1" },
  { label: "Misty Rose", hex: "#ffe4e1" },
  { label: "Pale Green", hex: "#98fb98" },
  { label: "Pale Turquoise", hex: "#afeeee" },
  { label: "Peachpuff", hex: "#ffdab9" },
  { label: "Thistle", hex: "#d8bfd8" },
];

// TODO: Replace below list of users with data obtained from backend for org; temp users for demo only
const TEMP_USERS_IN_REGION = [
  {
    name: "Patricia Smith",
    role: "Operator",
    region: "Northern Region",
    color: "Dark Salmon",
  },
  {
    name: "Jessica Brown",
    role: "Operator",
    region: "North Central",
    color: "Goldenrod",
  },
  {
    name: "John Miller",
    role: "Operator",
    region: "Inland Deserts Region",
    color: "Cadet Blue",
  },
  {
    name: "Emily Davis",
    role: "Operator",
    region: "Central Region",
    color: "Lawn Green",
  },
  {
    name: "Michael James",
    role: "Operator",
    region: "Bay Area",
    color: "Pale Turquoise",
  },
  {
    name: "Sarah Jones",
    role: "Operator",
    region: "South Coast Region",
    color: "Thistle",
  },
];

const AdminSettings = () => {
  // TODO: States should be obtained from backend on page load; replace hard-coded selections below
  const [selectedMainDisplay, setSelectedMainDisplay] = useState(
    "Continental United States"
  );
  const [selectedDisplayOptions, setSelectedDisplayOptions] = useState([
    "System Health",
    "Network Averages",
    "Severe Weather",
    "Status Legend",
  ]);

  const [inEditMode, setInEditMode] = useState(false);

  const UserRow = ({ data, i }) => {
    const [userColor, setUserColor] = useState(data.color);

    return (
      <TableRow
        key={`user-table-row-${i}`}
        sx={{
          "& td": {
            borderBottom: "2px solid white",
            fontSize: "max(12px, 0.75vw)",
          },
          "& td:first-child": {
            borderTopLeftRadius: "8px",
            borderBottomLeftRadius: "8px",
          },
          "& td:nth-child(3)": {
            borderTopRightRadius: "8px",
            borderBottomRightRadius: "8px",
          },
          "& td:last-child": {
            display: "table-cell",
            padding: "0.5vw",
          },
        }}
      >
        <TableCell
          sx={{
            bgcolor: TEMP_USER_COLOR_OPTIONS.find(
              (option) => option.label === userColor
            ).hex,
          }}
        >
          {data.name}
        </TableCell>
        <TableCell
          sx={{
            bgcolor: TEMP_USER_COLOR_OPTIONS.find(
              (option) => option.label === userColor
            ).hex,
          }}
        >
          {data.role}
        </TableCell>
        <TableCell
          sx={{
            bgcolor: TEMP_USER_COLOR_OPTIONS.find(
              (option) => option.label === userColor
            ).hex,
          }}
        >
          {data.region}
        </TableCell>
        {inEditMode && (
          <TableCell
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: "max(5px, 0.2vw)",
              textAlign: "center",
              width: "calc(max(16px, 1vw) * 6)",
            }}
          >
            <ColorMenu
              colorOptions={TEMP_USER_COLOR_OPTIONS}
              currColor={userColor}
              setUserColor={() => {}}
            />
            <EditIconContainer onClick={() => {}} src={EditIconBlack} />
            <TrashIconContainer onClick={() => {}} src={TrashIcon} />
          </TableCell>
        )}
      </TableRow>
    );
  };

  return (
    <Container>
      <Header>
        <HeaderText>Administrator Settings</HeaderText>
      </Header>

      {!inEditMode && (
        <ButtonGroup style={{ justifyContent: "right" }}>
          <ThemeButton onClick={() => setInEditMode(true)} type="button">
            <EditIcon fill="white" />
            Edit
          </ThemeButton>
        </ButtonGroup>
      )}

      <ContentContainer>
        <ContentRow>
          <FormControl sx={{ width: "50%" }}>
            <Label>Main Display Region</Label>
            <Select
              disabled={!inEditMode}
              // error={mainDisplayErr ? true : false}
              id="main-display-region-select"
              // labelId="main-display-region-select-label"
              onChange={(e) => setSelectedMainDisplay(e.target.value)}
              size="small"
              value={selectedMainDisplay}
            >
              {TEMP_MAIN_DISPLAY_OPTIONS &&
                TEMP_MAIN_DISPLAY_OPTIONS.map((option, i) => (
                  <MenuItem
                    key={`main-display-region-menu-item-${i}`}
                    value={option} // {option.id}
                  >
                    {option}
                    {/* option.codeName */}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <FormControl sx={{ width: "50%" }}>
            <Label>Enabled Display Elements</Label>
            <Select
              disabled={!inEditMode}
              // error={displayElementsErr ? true : false}
              id="enabled-display-elements-select"
              // labelId="enabled-display-elements-select-label"
              multiple={true}
              onChange={(e) => setSelectedDisplayOptions(e.target.value)}
              // renderOption={(props, option, { selected }) => {
              //   const { key, ...optionProps } = props;
              //   return (
              //     <li key={key} {...optionProps}>
              //       <CheckmarkIcon fill="rgba(0,0,0,0)" />
              //       {option}
              //     </li>
              //   );
              // }}
              size="small"
              value={selectedDisplayOptions}
            >
              {TEMP_ENABLED_DISPLAY_OPTIONS &&
                TEMP_ENABLED_DISPLAY_OPTIONS.map((option, i) => (
                  <MenuItem
                    key={`enabled-display-elements-menu-item-${i}`}
                    value={option} // option.id
                  >
                    <CheckmarkIcon fill="rgba(0,0,0,0)" />
                    {option}
                    {/* option.codeName */}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </ContentRow>

        <ContentRow>
          <LocationPreviewSection>
            <Label>Location Preview</Label>
            <MapCanvas>
              <APIProvider apiKey={"AIzaSyBy0cowEjTB0suohBM4h4UdiSz-ng-YPWg"}>
                <Map selectedPreview={selectedMainDisplay} />
              </APIProvider>
            </MapCanvas>
          </LocationPreviewSection>

          <UsersInRegionSection>
            {inEditMode && (
              <AddNewUserButton onClick={() => {}} type="button">
                + Add New User
              </AddNewUserButton>
            )}
            <Label>User(s) in Region</Label>
            <TableContainer
              id="users-in-region-table-container"
              sx={{
                bgcolor: "white",
                borderRadius: "8px",
                height: "50vh",
                padding: "0.5vw",
                width: "100%",
              }}
            >
              <Table sx={{ borderRadius: "8px" }}>
                <TableHead
                  id="users-in-region-table-head"
                  sx={{ borderBottom: "none" }}
                >
                  <TableRow
                    sx={{
                      "& th": {
                        borderBottom: "none",
                        fontSize: "max(16px, 0.85vw)",
                      },
                    }}
                  >
                    <TableCell align="left">
                      <b>Name</b>
                    </TableCell>
                    <TableCell align="left">
                      <b>Role</b>
                    </TableCell>
                    <TableCell align="left">
                      <b>Region</b>
                    </TableCell>
                    {inEditMode && <TableCell />}
                  </TableRow>
                </TableHead>
                <TableBody id="users-in-region-table-body">
                  {/* {isEmpty(users) ? (
                      <TableRow>
                        <TableCell sx={{ width: 50 }} />
                        <TableCell>No users found</TableCell>
                        <TableCell colSpan={inEditMode ? 4 : 3} />
                      </TableRow>
                    ) : ( */}
                  {TEMP_USERS_IN_REGION.map((user, i) => (
                    <UserRow data={user} i={i} key={`user-row-${i}`} />
                  ))}
                  {/* )} */}
                </TableBody>
              </Table>
            </TableContainer>
          </UsersInRegionSection>
        </ContentRow>
      </ContentContainer>

      {/* TODO: Add functionality to edit mode saving/cancelling */}
      {inEditMode && (
        <ButtonGroup>
          <ThemeButton onClick={() => setInEditMode(false)} type="button">
            Save
          </ThemeButton>
          <CancelButton onClick={() => setInEditMode(false)} type="button">
            Cancel
          </CancelButton>
        </ButtonGroup>
      )}
    </Container>
  );
};

export default AdminSettings;
